/**
 * The current version of the SkillData schema.
 * This is used to track changes to the data schema and ensure backwards compatibility.
 */
export const SKILL_DATA_VERSION = 4;
/**
 * Blank SkillData object used as a placeholder before actual tree data is loaded.
 * This should only be used internally by TreeInteractor before data is loaded.
 */
export const BLANK_TREE_DATA = {
    nodes: [],
    links: [],
    documents: [],
    viewport: { x: 0, y: 0, k: 1 },
    selectedNodeId: null,
    initialViewport: { x: 0, y: 0, k: 1 },
    version: SKILL_DATA_VERSION,
    id: '',
    name: '',
    isPrivate: false
};
/**
 * Initial blank SkillData object with all required properties.
 * This represents a new, empty skill tree with default values.
 * Used when creating a new tree (not as a placeholder).
 */
export const INITIAL_DATA = {
    nodes: [],
    links: [],
    documents: [],
    viewport: { x: 0, y: 0, k: 1 },
    selectedNodeId: null,
    initialViewport: { x: 0, y: 0, k: 1 },
    version: SKILL_DATA_VERSION,
    id: '',
    name: 'Untitled',
    isPrivate: false
};
/**
 * Type guard function to validate SkillData objects.
 * This ensures all required properties are present and have the correct types.
 */
export function isValidSkillData(data) {
    if (!data || typeof data !== 'object') {
        return false;
    }
    // Check required properties
    if (!Array.isArray(data.nodes))
        return false;
    if (!Array.isArray(data.links))
        return false;
    if (!Array.isArray(data.documents))
        return false;
    for (const doc of data.documents) {
        if (!doc || typeof doc !== 'object')
            return false;
        const d = doc;
        if (typeof d.nodeId !== 'string')
            return false;
        if (d.documentPath === undefined || d.documentPath === null) {
            continue;
        }
        if (typeof d.documentPath !== 'string')
            return false;
        if (d.documentPath.trim() === '')
            return false;
    }
    if (!data.viewport || typeof data.viewport !== 'object')
        return false;
    if (typeof data.viewport.x !== 'number')
        return false;
    if (typeof data.viewport.y !== 'number')
        return false;
    if (typeof data.viewport.k !== 'number')
        return false;
    if (data.selectedNodeId !== null && data.selectedNodeId !== undefined && typeof data.selectedNodeId !== 'string')
        return false;
    if (!data.initialViewport || typeof data.initialViewport !== 'object')
        return false;
    if (typeof data.initialViewport.x !== 'number')
        return false;
    if (typeof data.initialViewport.y !== 'number')
        return false;
    if (typeof data.initialViewport.k !== 'number')
        return false;
    if (typeof data.version !== 'number')
        return false;
    if (typeof data.id !== 'string')
        return false;
    if (typeof data.name !== 'string')
        return false;
    if (typeof data.isPrivate !== 'boolean')
        return false;
    return true;
}
/**
 * Entitlement enum for a skill tree.
 * Use these enum values instead of string literals for type safety.
 */
export var Entitlement;
(function (Entitlement) {
    Entitlement["Owner"] = "owner";
    Entitlement["Editor"] = "editor";
    Entitlement["Commenter"] = "commenter";
    Entitlement["Viewer"] = "viewer";
    Entitlement["None"] = "none";
})(Entitlement || (Entitlement = {}));
